import React, { useEffect, useState } from 'react';
import { CloseIcon } from './icons';
import {
  IS_MAC, OPEN_SHORTCUTS_EVENT, SHORTCUTS, formatShortcut, shortcutKeys, useShortcut, useShortcutHints,
} from '../hooks/useKeyboardShortcuts';

export const openShortcutsDialog = () => window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT));

// Clicking the real button reuses its "Copied" feedback instead of duplicating it.
const copyLastResponse = () => {
  const buttons = document.querySelectorAll<HTMLButtonElement>('[data-action="copy-response"]');
  buttons[buttons.length - 1]?.click();
};

const focusMessageBox = () => {
  document.querySelector<HTMLTextAreaElement>('textarea[data-chat-input]')?.focus();
};

const KeyboardShortcuts: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const hintsVisible = useShortcutHints();

  useShortcut(SHORTCUTS.showShortcuts, () => setIsOpen((open) => !open));
  useShortcut(SHORTCUTS.copyLastResponse, copyLastResponse);
  useShortcut(SHORTCUTS.focusInput, focusMessageBox);

  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_SHORTCUTS_EVENT, open);

    return () => window.removeEventListener(OPEN_SHORTCUTS_EVENT, open);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return (
    <>
      {hintsVisible && !isOpen && (
        <div className="cv-shortcut-pill" aria-hidden="true">
          <kbd className="cv-kbd">{formatShortcut(SHORTCUTS.showShortcuts)}</kbd>
          All shortcuts
        </div>
      )}

      {isOpen && (
        <div className="cv-modal-backdrop" onClick={() => setIsOpen(false)}>
          <div
            className="cv-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cv-shortcuts-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cv-modal__header">
              <h2 id="cv-shortcuts-title" className="cv-modal__title">Keyboard shortcuts</h2>
              <button type="button" className="cv-icon-btn" onClick={() => setIsOpen(false)} aria-label="Close">
                <CloseIcon />
              </button>
            </div>
            <ul className="cv-shortcut-list">
              {Object.values(SHORTCUTS).map((shortcut) => (
                <li key={shortcut.code} className="cv-shortcut-list__row">
                  <span>{shortcut.label}</span>
                  <span className="cv-shortcut-list__keys">
                    {shortcutKeys(shortcut).map((key) => <kbd key={key} className="cv-kbd">{key}</kbd>)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="cv-modal__footnote">Hold {IS_MAC ? '⌘' : 'Ctrl'} to see shortcuts next to the buttons.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default KeyboardShortcuts;
